import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';
import AddFundsModal from './AddFundsModal';

const formatCurrency = (amount) => {
  if (amount == null) return '0.00';
  return Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const WalletBalanceSection = () => {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddFunds, setShowAddFunds] = useState(false);

  const fetchWallet = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/wallet/balance');
      setWallet(res.data?.data || null);
    } catch (err) {
      console.error('Failed to fetch wallet balance:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);

  const handleAddFundsSuccess = () => {
    setShowAddFunds(false);
    fetchWallet();
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-6 animate-pulse">
          <div className="space-y-2">
            <div className="h-4 w-28 bg-gray-100 rounded" />
            <div className="h-8 w-36 bg-gray-100 rounded" />
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-6 animate-pulse">
          <div className="space-y-2">
            <div className="h-4 w-28 bg-gray-100 rounded" />
            <div className="h-6 w-full bg-gray-100 rounded" />
            <div className="h-6 w-full bg-gray-100 rounded" />
            <div className="h-6 w-full bg-gray-100 rounded" />
          </div>
        </div>
      </div>
    );
  }

  const balance = wallet?.balance ?? 0;
  const isLow = balance < 500;
  const usage = {
    aiCalls: wallet?.usageBreakdown?.aiCalls ?? 0,
    phoneRental: wallet?.usageBreakdown?.phoneRental ?? 0,
    messages: wallet?.usageBreakdown?.messages ?? 0,
  };
  const totalSpent = Object.values(usage).reduce((sum, v) => sum + v, 0);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Wallet Balance */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Icon name="Wallet" size={18} className="text-gray-500" />
              Wallet Balance
              <div className="relative group">
                <Icon name="Info" size={14} className="text-gray-400 cursor-pointer hover:text-gray-600 transition-colors" />
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg py-3 px-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-30">
                  <p className="text-[10px] text-gray-500 uppercase font-medium tracking-wider mb-2">Our Rates</p>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon name="Phone" size={12} className="text-blue-500" />
                        <span className="text-xs text-gray-500">AI Call</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-900">₹{wallet?.aiCallRate ?? 2}/30 sec</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon name="Smartphone" size={12} className="text-green-500" />
                        <span className="text-xs text-gray-500">Phone Rental</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-900">₹{wallet?.phoneRentalRate ?? 1000}/no.</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon name="MessageSquare" size={12} className="text-purple-500" />
                        <span className="text-xs text-gray-500">Message</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-900">₹{wallet?.messageRate ?? 1}/msg</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2 pt-2 border-t border-gray-100">18% GST is charged when you add funds, not on usage.</p>
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-b-white" />
                </div>
              </div>
            </h3>
            <button
              onClick={() => setShowAddFunds(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#2F55FF] text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Icon name="Plus" size={15} />
              Add Funds
            </button>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Available</p>
            <div className="flex items-baseline gap-1">
              <span className="text-sm text-gray-500">₹</span>
              <span className={`text-3xl font-bold ${isLow ? 'text-red-600' : 'text-gray-900'}`}>
                {formatCurrency(balance)}
              </span>
            </div>
            {isLow && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <Icon name="AlertTriangle" size={12} />
                Low balance
              </p>
            )}
          </div>
        </div>

        {/* This Month Spent */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Icon name="TrendingUp" size={18} className="text-gray-500" />
              This Month
            </h3>
            <span className="text-xs text-gray-400">
              Total: <span className="font-medium text-gray-600">₹{formatCurrency(totalSpent)}</span>
            </span>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-md flex items-center justify-center bg-blue-50 flex-shrink-0">
                <Icon name="Bot" size={14} className="text-blue-600" />
              </div>
              <span className="text-sm text-gray-700 flex-1">AI Calls</span>
              <span className="text-sm font-medium text-gray-900">₹{formatCurrency(usage.aiCalls)}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-md flex items-center justify-center bg-violet-50 flex-shrink-0">
                <Icon name="Phone" size={14} className="text-violet-600" />
              </div>
              <span className="text-sm text-gray-700 flex-1">Phone Rental</span>
              <span className="text-sm font-medium text-gray-900">₹{formatCurrency(usage.phoneRental)}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-md flex items-center justify-center bg-emerald-50 flex-shrink-0">
                <Icon name="MessageSquare" size={14} className="text-emerald-600" />
              </div>
              <span className="text-sm text-gray-700 flex-1">Messages</span>
              <span className="text-sm font-medium text-gray-900">₹{formatCurrency(usage.messages)}</span>
            </div>
          </div>
        </div>
      </div>

      {showAddFunds && (
        <AddFundsModal
          gstOnFile={wallet?.gstOnFile}
          onClose={() => setShowAddFunds(false)}
          onSuccess={handleAddFundsSuccess}
        />
      )}
    </>
  );
};

export default WalletBalanceSection;
